import React from 'react';
import { motion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';
import { cn } from '@/shared/utils/cn';

interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'success' | 'default';
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'sm',
  className = '',
  children,
  ...props
}) => {
  const handleTouchStart = () => {
    // Haptic feedback for touch devices
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  const baseStyles = "inline-flex items-center justify-center font-semibold transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg rounded-lg min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 relative overflow-hidden";

  const variants = {
    primary: cn(
      "bg-gradient-to-r from-brand-600 to-brand-500 active:from-brand-700 active:to-brand-600 lg:hover:from-brand-500 lg:hover:to-brand-400 text-white border border-brand-500/20 shadow-md shadow-brand-900/20 lg:hover:shadow-lg lg:hover:shadow-brand-900/30 focus:ring-brand-400",
      "theme-dynamics:shadow-elevation-2",
      "theme-dynamics:hover:shadow-elevation-3",
      "theme-dynamics:rounded-medium",
      "theme-dynamics:transition-fast"
    ),
    default: cn(
      "bg-gradient-to-r from-brand-600 to-brand-500 active:from-brand-700 active:to-brand-600 lg:hover:from-brand-500 lg:hover:to-brand-400 text-white border border-brand-500/20 shadow-md shadow-brand-900/20 lg:hover:shadow-lg lg:hover:shadow-brand-900/30 focus:ring-brand-400",
      "theme-dynamics:shadow-elevation-2",
      "theme-dynamics:hover:shadow-elevation-3",
      "theme-dynamics:rounded-medium",
      "theme-dynamics:transition-fast"
    ),
    secondary: cn(
      "bg-dark-surface/80 backdrop-blur-sm active:bg-dark-bg lg:hover:bg-dark-surface text-dark-text border border-dark-border/30 lg:hover:border-dark-border/30 shadow-sm lg:hover:shadow-md focus:ring-gray-500",
      "theme-dynamics:shadow-elevation-1",
      "theme-dynamics:hover:shadow-elevation-2",
      "theme-dynamics:rounded-medium",
      "theme-dynamics:transition-fast"
    ),
    ghost: cn(
      "bg-transparent active:bg-dark-surface/60 lg:hover:bg-dark-surface/40 text-dark-muted lg:hover:text-dark-text border border-transparent lg:hover:border-dark-border/20 focus:ring-gray-500",
      "theme-dynamics:hover:shadow-elevation-1",
      "theme-dynamics:rounded-medium",
      "theme-dynamics:transition-fast"
    ),
    outline: cn(
      "bg-transparent active:bg-dark-surface/60 lg:hover:bg-dark-surface/40 text-dark-text border border-dark-border/40 lg:hover:border-dark-border/60 shadow-sm focus:ring-gray-500",
      "theme-dynamics:hover:shadow-elevation-1",
      "theme-dynamics:rounded-medium",
      "theme-dynamics:transition-fast"
    ),
    success: cn(
      "bg-gradient-to-r from-emerald-700 to-emerald-600 active:from-emerald-800 active:to-emerald-700 lg:hover:from-emerald-600 lg:hover:to-emerald-500 text-white border border-emerald-500/30 shadow-sm lg:hover:shadow-md lg:hover:shadow-emerald-900/20 focus:ring-emerald-400",
      "theme-dynamics:shadow-elevation-1",
      "theme-dynamics:hover:shadow-elevation-2",
      "theme-dynamics:rounded-medium",
      "theme-dynamics:transition-fast"
    ),
    danger: cn(
      "bg-gradient-to-r from-red-900/60 to-red-800/40 active:from-red-900 active:to-red-800 lg:hover:from-red-900/80 lg:hover:to-red-800/60 text-red-200 border border-red-700/40 lg:hover:border-red-600/60 shadow-sm lg:hover:shadow-md lg:hover:shadow-red-900/20 focus:ring-red-400",
      "theme-dynamics:shadow-elevation-1",
      "theme-dynamics:hover:shadow-elevation-2",
      "theme-dynamics:rounded-medium",
      "theme-dynamics:transition-fast"
    ),
  };

  const sizes = {
    xs: "px-2 py-1 text-xs md:px-2 md:py-1",
    sm: "px-3 py-1.5 text-xs md:px-3 md:py-1.5",
    md: "px-4 py-2 text-sm md:px-4 md:py-2",
    lg: "px-5 py-2.5 text-sm md:px-5 md:py-2.5",
  };

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      transition={{
        duration: 0.15,
        ease: [0.4, 0, 0.2, 1]
      }}
      onTouchStart={handleTouchStart}
      // Default data attributes - can be overridden by props
      data-testid='unknown-action-btn'
      data-ux='action'
      data-interaction-id='unknown-action-btn'
      // Spread props AFTER defaults so they can override
      {...props}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </motion.button>
  );
};
